import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { areaLabel, useAreas } from '../data/geo.ts'
import { BackLink, Banner, DisabledHint, errorText, Field } from '../ui/components.tsx'
import { useClub } from '../club/ClubProvider.tsx'
import { useWriteAccess } from '../ui/hooks.ts'

interface Feat { index: number; label: string; geometry: { type: string; coordinates: unknown[] }; key: string }
const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '')

/** GeoJSON from QGIS (or any GIS) into existing areas: each polygon is matched to an area by its code or name, the club confirms, and every match
 *  becomes a NEW VERSION of that area's shape (the old one is kept). Polygons with no match are listed and left out, never guessed. */
export function CourseImportPage() {
  const { t, i18n } = useTranslation()
  const edit = useWriteAccess('area.edit')
  const { sendAndWait } = useClub()
  const areas = useAreas()
  const [feats, setFeats] = useState<Feat[] | null>(null)
  const [bad, setBad] = useState<string | null>(null)
  const [choice, setChoice] = useState<Record<number, string>>({})
  const [accuracy, setAccuracy] = useState('1')
  const [progress, setProgress] = useState<{ done: number; failed: Array<{ label: string; code: string }> } | null>(null)
  const [running, setRunning] = useState(false)

  const lookup = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of areas ?? []) {
      if (a.archived_at) continue
      if (a.code) m.set(norm(a.code), a.id)
      for (const n of [a.name.fr, a.name.en]) if (n) m.set(norm(n), a.id)
    }
    return m
  }, [areas])

  const onFile = async (f: File | undefined) => {
    setBad(null); setFeats(null); setProgress(null)
    if (!f) return
    try {
      const j = JSON.parse(await f.text()) as { type?: string; features?: Array<{ geometry?: { type: string; coordinates: unknown[] }; properties?: Record<string, unknown> | null }> }
      const list = j.type === 'FeatureCollection' ? j.features ?? [] : []
      const out: Feat[] = []
      list.forEach((ft, index) => {
        const g = ft.geometry
        if (!g || (g.type !== 'Polygon' && g.type !== 'MultiPolygon')) return
        const p = ft.properties ?? {}
        const label = String(p.code ?? p.CODE ?? p.ref ?? p.REF ?? p.name ?? p.nom ?? p.NOM ?? p.id ?? `#${index + 1}`)
        out.push({ index, label, geometry: g, key: norm(label) })
      })
      if (out.length === 0) { setBad(t('courseImport.nothing')); return }
      setFeats(out)
      setChoice(Object.fromEntries(out.map((o) => [o.index, lookup.get(o.key) ?? ''])))
    } catch { setBad(t('courseImport.unreadable')) }
  }

  const chosen = (feats ?? []).filter((f) => choice[f.index])
  const accNum = Number(accuracy.replace(',', '.'))
  const accOk = Number.isFinite(accNum) && accNum >= 0 && accNum <= 1000
  const run = async () => {
    setRunning(true); const failed: Array<{ label: string; code: string }> = []; let done = 0
    setProgress({ done: 0, failed: [] })
    for (const f of chosen) {
      try {
        const v = await sendAndWait('area.revise@1', { area_id: choice[f.index]!, version_id: newId(), geom: f.geometry, source: 'import', accuracy_m: accNum, note: `GeoJSON ${f.label}`.slice(0, 500) } as never)
        if (v.status === 'applied' || v.status === 'applied_with_exception') done++; else failed.push({ label: f.label, code: v.status === 'queued' ? 'NETWORK' : v.code ?? 'UNKNOWN' })
      } catch { failed.push({ label: f.label, code: 'UNKNOWN' }) }
      setProgress({ done, failed: [...failed] })
    }
    setRunning(false)
  }

  return (
    <section className="flex flex-col gap-4" data-testid="course-import-page">
      <BackLink to="/course">← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('courseImport.title')}</h1>
      <p className="meta">{t('courseImport.help')}</p>
      {!edit.permitted && <Banner tone="warn">{t('shape.notAllowed')}</Banner>}
      <Field label={t('courseImport.file')} htmlFor="geojson-file"><input id="geojson-file" type="file" accept=".geojson,.json,application/geo+json,application/json" className="input" onChange={(e) => void onFile(e.target.files?.[0])} data-testid="geojson-file" /></Field>
      {bad && <Banner tone="error">{bad}</Banner>}
      {feats && (
        <>
          <ul className="flex flex-col gap-2" data-testid="import-rows">
            {feats.map((f) => (
              <li key={f.index} className="card flex flex-wrap items-center gap-3" data-testid="import-row">
                <span className="font-semibold">{f.label}</span>
                <select className="input !min-h-11 flex-1" aria-label={t('courseImport.target')} value={choice[f.index] ?? ''} onChange={(e) => setChoice({ ...choice, [f.index]: e.target.value })} data-testid="import-target">
                  <option value="">{t('courseImport.skip')}</option>
                  {(areas ?? []).filter((a) => !a.archived_at && a.area_type_code !== 'site_root').map((a) => <option key={a.id} value={a.id}>{areaLabel(a, i18n.language)}{a.code ? ` (${a.code})` : ''}</option>)}
                </select>
              </li>
            ))}
          </ul>
          <Field label={t('shape.accuracy')} hint={t('courseImport.accuracyHelp')} htmlFor="import-accuracy"><input id="import-accuracy" className="input" inputMode="decimal" value={accuracy} onChange={(e) => setAccuracy(e.target.value)} data-testid="import-accuracy" /></Field>
          <DisabledHint show={edit.allowed && chosen.length === 0}>{t('courseImport.pickOne')}</DisabledHint>
          <button className="btn btn-primary" disabled={!edit.allowed || chosen.length === 0 || !accOk || running} onClick={() => void run()} data-testid="import-go">{t('courseImport.go', { count: chosen.length })}</button>
        </>
      )}
      {progress && (
        <div className="flex flex-col gap-2" data-testid="import-result">
          <Banner tone={progress.failed.length ? 'warn' : 'ok'}>{t('courseImport.done', { done: progress.done, failed: progress.failed.length })}</Banner>
          {progress.failed.map((f, i) => <p key={i} className="text-sm text-red-700">{f.label}: {errorText(t, f.code)}</p>)}
        </div>
      )}
    </section>
  )
}
